import {CUSTOMER_SEGMENT, PRODUCT_FAMILY} from '@pandora/data-model-product/lib/types';

(function () {
  /**
   * @ngdoc factory
   * @name binky.core.products.usage:ProductUsage
   * @description Model for a Product Usage across a set of products
   */
  angular.module('app.core.products.usage').factory('ProductUsage', factory);

  /* @ngInject */
  function factory(_, feature, FULFILLABLE_ITEM_CODE, PRODUCT_CHANNELS) {
    class ProductUsage {
      /**
       * @class
       * @description Creates a new Contract for use.
       *
       * @param {Object} [options] Initialization Object (params described below)
       * @param {ConsumableSummarizationList} [options.consumableSummarizationList] The list of summarized consumption
       * @param {Array} [options.legacyProductList] The legacy product objects, used purely for non-SDL device usage
       * @param {Array} [options.productList] The array of products to process
       */
      constructor(options) {
        parse(this, options);
      }

      /**
       * @description Checks if there are any user, device, or consumable licenses
       *
       * @return {Boolean} true if this model has any products
       */
      hasAny() {
        return (
          this.consumable.items.length > 0 ||
          this.device.items.length > 0 ||
          this.namedUser.items.length > 0
        );
      }

      /**
       * @description Checks if there are any consumable licenses
       *
       * @return {Boolean} true if this model has any consumable products
       */
      hasConsumable() {
        return this.consumable.items.length > 0;
      }

      /**
       * @description Checks if there are any device licenses
       *
       * @return {Boolean} true if this model has any device products
       */
      hasDevice() {
        return this.device.items.length > 0;
      }

      /**
       * @description Checks if there are any user licenses
       *
       * @return {Boolean} true if this model has any user products
       */
      hasNamedUser() {
        return this.namedUser.items.length > 0;
      }

      /**
       * @description Checks if there are no user, device, or consumable licenses
       *
       * @return {Boolean} true if this model has no products
       */
      hasNone() {
        return (
          this.consumable.items.length === 0 &&
          this.device.items.length === 0 &&
          this.namedUser.items.length === 0
        );
      }
    }

    /** Return reference to Constructor function **/
    return ProductUsage;

    function parse(model, options) {
      model.totals = baseLicenseModel();
      model.consumable = _.assign(baseLicenseModel(), {fulfillableItems: []});
      model.device = baseLicenseModel();
      model.namedUser = baseLicenseModel();

      // eslint-disable-next-line complexity,max-statements
      _.forEach(options.productList, (product) => {
        let legacyProduct = _.find(
          options.legacyProductList,
          (foundLegacyProduct) => _.get(foundLegacyProduct, 'product.offerId') === product.offerId
        );
        const displaySignAddons = feature.isEnabled('temp_sign_addon')
          ? false // This means we will calc attributes for Sign KBA, Phone Auth and display.
          : !_.some(product.customerUI, (ui) => _.includes(PRODUCT_CHANNELS, ui)); // we skip Sign KBA and Sign phone auth.

        const displayEnterpriseSCPConsumables = feature.isEnabled('temp_enterprise_scp');

        if (displaySignAddons) {
          // we do nothing here, as we want to exclude products not administered in AAC (like Sign KBA and Phone Auth)
        } else if (product.isLegacyDeviceLicense()) {
          if (!legacyProduct) {
            legacyProduct = _.find(
              options.legacyProductList,
              (foundLegacyProduct) =>
                _.get(foundLegacyProduct, 'product.code') === product.code &&
                _.get(foundLegacyProduct, 'product.licenseType') === 'MACHINE_ONLY'
            );
          }
          // Legacy Device Licenses
          const modelItem = modelItemFromLegacyData(product, legacyProduct);
          addModelToTarget(model.device, modelItem);
          model.device.items.push(modelItem);
        } else if (product.fulfillableItemList.hasLaboratoryLicenseManagement()) {
          const consumableSummary = options.consumableSummarizationList.getConsumablesForSummaryId(
            product.id
          );
          // Shared Device Licenses
          const modelItem = _.assign(
            modelItemFromBaseConsumptionData(
              _.find(consumableSummary, {
                fulfillableItemCode: FULFILLABLE_ITEM_CODE.APPS_OFFLINE_PROFILES_COUNT,
              })
            ),
            // we override 'paid' and 'unpaid' so they always reflects the license value
            _.pick(modelItemFromProduct(product), [
              'languages',
              'paid',
              'product',
              'unlimited',
              'unpaid',
            ])
          );
          addModelToTarget(model.device, modelItem);
          model.device.items.push(modelItem);
        } else if (isConsumableProduct(product, displayEnterpriseSCPConsumables)) {
          // Consumption-based Licenses
          let foundSummaries, modelItem;
          const stockCreditPackFICodes = displayEnterpriseSCPConsumables
            ? [FULFILLABLE_ITEM_CODE.STOCK_CREDIT, FULFILLABLE_ITEM_CODE.STOCK_UNIVERSAL_CREDITS]
            : [FULFILLABLE_ITEM_CODE.STOCK_CREDIT];
          const currentStockCreditPackItem = _.find(product.fulfillableItemList.items, (item) =>
            _.includes(stockCreditPackFICodes, item.code)
          );

          const currentStockCreditPackType = currentStockCreditPackItem
            ? currentStockCreditPackItem.code
            : undefined;

          if (currentStockCreditPackType) {
            // Stock credits are reliant on the contract-level summary
            foundSummaries = options.consumableSummarizationList.getConsumablesForSummaryId(
              product.contractId
            );

            // eslint-disable-next-line angular/function-type
            foundSummaries = _.filter(
              foundSummaries,
              ({fulfillableItemCode}) => fulfillableItemCode === currentStockCreditPackType
            );

            modelItem = _.find(model.consumable.items, (parentItem) =>
              _.some(
                parentItem.items,
                (item) =>
                  item.fulfillableItem &&
                  _.includes(stockCreditPackFICodes, item.fulfillableItem.code)
              )
            );
          } else {
            foundSummaries = options.consumableSummarizationList.getConsumablesForSummaryId(
              product.id
            );
          }
          // we only need to add the item if it doesn't already exist
          if (_.isEmpty(modelItem)) {
            modelItem = _.assign(baseLicenseModel(), {
              legacyProduct,
              offerNames: {},
              product,
            });
            modelItem.offerNames[product.offerId] = product.longName;
            modelItem.items = _.map(foundSummaries, (summaryItem) => {
              const fulfillableItem = _.find(product.fulfillableItemList.items, {
                code: summaryItem.fulfillableItemCode,
              });
              const summaryModelItem = modelItemFromConsumptionData(
                modelItem,
                product,
                fulfillableItem,
                summaryItem
              );
              addModelToTarget(modelItem, summaryModelItem);
              return summaryModelItem;
            });
            addModelToTarget(model.consumable, modelItem);
            model.consumable.items.push(modelItem);
            // we explode the fulfillable items, to make future repeats simpler
            model.consumable.fulfillableItems = _.union(
              model.consumable.fulfillableItems,
              modelItem.items
            );
          } else {
            // if the modelItem already exists, we add any additional offer Ids to the list
            modelItem.offerNames[product.offerId] = product.longName;
          }
        } else {
          // Named User Licenses
          const modelItem = modelItemFromProduct(product, legacyProduct);
          addModelToTarget(model.namedUser, modelItem);
          model.namedUser.items.push(modelItem);
        }
      });

      addModelToTarget(model.totals, model.device);
      addModelToTarget(model.totals, model.namedUser);
    }

    function isConsumableProduct(product, displayEnterpriseSCPConsumables) {
      return (
        product.isConsumable() ||
        (displayEnterpriseSCPConsumables &&
          product.family === PRODUCT_FAMILY.STOCK_ASSETS &&
          product.customerSegment === CUSTOMER_SEGMENT.ENTERPRISE)
      );
    }

    function baseLicenseModel() {
      return {
        active: 0,
        available: 0,
        items: [],
        overActivated: 0,
        paid: 0,
        total: 0,
        unlimited: false,
        unpaid: 0,
      };
    }

    function modelItemFromBaseConsumptionData(consumptionSummary) {
      return {
        active: _.get(consumptionSummary, 'consumedQuantity', 0),
        available: _.get(consumptionSummary, 'remainingQuantity', 0),
        overActivated: _.get(consumptionSummary, 'overConsumedQuantity', 0),
        paid: _.get(consumptionSummary, 'totalQuantity', 0),
        total: _.get(consumptionSummary, 'totalQuantity', 0),
        unlimited: false,
        unpaid: 0,
      };
    }

    function modelItemFromConsumptionData(
      parentItem,
      product,
      fulfillableItem,
      consumptionSummary
    ) {
      const result = _.assign(modelItemFromBaseConsumptionData(consumptionSummary), {
        active: _.get(consumptionSummary, 'consumedQuantity', 0),
        available: _.get(consumptionSummary, 'remainingQuantity', 0),
        consumables: _.map(consumptionSummary.consumableUsageList.items, (usage) => ({
          consumed: _.get(usage, 'consumedQuantity', 0),
          expirationDate: usage.expirationDate,
          // when the usage has no name, we fall back to the product name
          // this is necessary for Sign Transactions
          name: () => usage.name || parentItem.offerNames[usage.offerId] || product.longName,
          remaining: _.get(usage, 'remainingQuantity', 0),
          total: _.get(usage, 'totalQuantity', 0),
          unpaid: 0,
        })),
        consumablesCount: consumptionSummary.usageTotal,
        consumptionSummary,
        fulfillableItem,
        hasMoreConsumables:
          consumptionSummary.usageTotal > consumptionSummary.consumableUsageList.items.length,
        offerNames: {},
        overActivated: _.get(consumptionSummary, 'overConsumedQuantity', 0),
        paid: _.get(consumptionSummary, 'totalQuantity', 0),
        parentItem,
        total: _.get(consumptionSummary, 'totalQuantity', 0),
        unlimited: false,
        unpaid: 0,
      });
      result.consumed = _(result.consumables).map('consumed').sum();
      return result;
    }

    function modelItemFromLegacyData(product, legacyProduct) {
      const result = {
        active: _.get(legacyProduct, 'activeSeatCount', 0),
        languages: _.union([], _.get(product, 'offerQuantityList.items')),
        legacyProduct,
        paid: _.get(legacyProduct, 'paidSeatCount', 0),
        product,
        unlimited: false,
        unpaid: _.get(legacyProduct, 'unpaidSeatCount', 0),
      };
      result.total = result.paid + result.unpaid;
      result.available = Math.max(result.total - result.active, 0);
      result.overActivated = Math.max(result.active - result.total, 0);
      // if no languages exist, we assume all quantity is multi-language
      if (_.isEmpty(result.languages)) {
        result.languages.push({language: 'MULT', quantity: result.paid});
      }
      return result;
    }

    function modelItemFromProduct(product, legacyProduct) {
      const result = {
        // CAUIP-10335 For FRL license we need to look at licenseActivation.totalActivationCount.
        active:
          _.get(product, 'licenseActivation.totalActivationCount', 0) ||
          _.get(product, 'provisionedQuantity', 0),
        languages: _.union([], _.get(product, 'offerQuantityList.items')),
        legacyProduct,
        // For parity with what the customer response API is doing, we only count LQs that are PAID or renewing.
        paid: _.invoke(product, 'licenseQuantityList.getPaidOrRenewingQuantity'),
        product,
        unlimited: _.invoke(product, 'licenseQuantityList.hasUnlimitedLicenses'),
        unpaid: _.invoke(
          product,
          'licenseQuantityList.getPendingPaymentOrGracePastDueOrPastDueQuantity'
        ),
      };
      result.total = result.paid + result.unpaid;
      result.available = Math.max(result.total - result.active, 0);
      result.overActivated = Math.max(result.active - result.total, 0);
      // if no languages exist, we assume all quantity is multi-language
      if (_.isEmpty(result.languages)) {
        result.languages.push({language: 'MULT', quantity: result.paid});
      }
      return result;
    }

    function addModelToTarget(targetModel, sourceModel) {
      targetModel.active += sourceModel.active;
      targetModel.available += sourceModel.available;
      targetModel.overActivated += sourceModel.overActivated;
      targetModel.paid += sourceModel.paid;
      targetModel.total += sourceModel.total;
      targetModel.unlimited = targetModel.unlimited || sourceModel.unlimited;
      targetModel.unpaid += sourceModel.unpaid;
    }
  }
})();
